import FetchService from "@core-ui/ApiServices/FetchService";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { persistCatalogPropertyOrder } from "@ext/properties/logic/persistCatalogPropertyOrder";
import { reorderProperties } from "@ext/properties/logic/reorderProperties";
import { useCallback, useRef, useState } from "react";

export const useReorderCatalogProperties = () => {
	const { catalogProps, updateCatalogProps } = useCatalogPropsStore(
		(state) => ({ catalogProps: state.data, updateCatalogProps: state.update }),
		"shallow",
	);
	const apiUrlCreator = ApiUrlCreator.value;
	const isSavingRef = useRef(false);
	const [isSaving, setIsSaving] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: context-service instances are stable per catalog
	const reorder = useCallback(
		async (activeId: string, overId: string) => {
			if (!catalogProps || isSavingRef.current) return false;
			const properties = catalogProps.properties ?? [];
			const reorderedProperties = reorderProperties(properties, activeId, overId);
			if (reorderedProperties === properties) return false;

			isSavingRef.current = true;
			setIsSaving(true);
			try {
				return await persistCatalogPropertyOrder(catalogProps, reorderedProperties, {
					save: async (props) => {
						const response = await FetchService.fetch(
							apiUrlCreator.updateCatalogProps(),
							JSON.stringify(props),
							MimeTypes.json,
						);
						return response.ok;
					},
					update: (nextProperties) => updateCatalogProps({ properties: nextProperties }),
				});
			} finally {
				isSavingRef.current = false;
				setIsSaving(false);
			}
		},
		[apiUrlCreator, catalogProps, updateCatalogProps],
	);

	return { isSaving, reorder };
};
